import { recordBlogStage } from './blogConversion';
import { trackMetaOnce } from './metaAnalytics';
import type { SupabaseClient } from '@supabase/supabase-js';

const FIRST_TOUCH_KEY = 'cartlens_first_touch_v1';
const MAX_FIELD = 160;
const MAX_PATH = 500;

type FirstTouch = {
  source: string;
  medium: string;
  campaign: string | null;
  landing_page: string;
  captured_at: string;
};

const clean = (value: string | null, limit = MAX_FIELD) =>
  value?.trim().slice(0, limit) || null;

// Capture the first known visit before navigation to Clerk's sign-up flow.
// This is first-touch attribution on this browser, not a claim of perfect cross-device attribution.
export function captureFirstTouch(): void {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(FIRST_TOUCH_KEY)) return;
    const params = new URLSearchParams(window.location.search);
    const source = clean(params.get('utm_source'));
    const medium = clean(params.get('utm_medium'));
    const campaign = clean(params.get('utm_campaign'));
    let referrer: URL | null = null;
    try { if (document.referrer) referrer = new URL(document.referrer); } catch { /* ignore */ }
    const isExternal = referrer
      && referrer.hostname.replace(/^www\./, '') !== window.location.hostname.replace(/^www\./, '');
    const search = isExternal && /(^|\.)(google|bing|yahoo|duckduckgo)\./i.test(referrer.hostname);
    const firstTouch: FirstTouch = {
      source: source || (isExternal ? referrer!.hostname : 'direct'),
      medium: medium || (search ? 'organic_search' : isExternal ? 'referral' : 'none'),
      campaign,
      landing_page: window.location.pathname.slice(0, MAX_PATH),
      captured_at: new Date().toISOString(),
    };
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(firstTouch));
  } catch { /* storage can be disabled; tracking must never block the app */ }
}

export function trackGA(name: string, params: Record<string, string | number> = {}): void {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }
}

// The insert and primary key make this idempotent across reloads and devices.
// Existing accounts are not retroactively counted as new signups.
export async function recordSignup(
  supabase: SupabaseClient,
  userId: string,
  createdAt: Date | null | undefined,
): Promise<void> {
  if (!createdAt || Date.now() - new Date(createdAt).getTime() > 24 * 60 * 60 * 1000) return;
  recordBlogStage('signup_complete', userId, createdAt);
  let touch: FirstTouch | null = null;
  try { touch = JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || 'null'); } catch { /* ignore */ }
  if (!touch || new Date(touch.captured_at).getTime() > new Date(createdAt).getTime() + 60_000) return;
  const { error } = await supabase.from('user_acquisition').insert({
    user_id: userId,
    first_source: touch.source,
    first_medium: touch.medium,
    first_campaign: touch.campaign,
    first_landing_page: touch.landing_page,
    first_seen_at: touch.captured_at,
  });
  if (!error) {
    trackGA('sign_up', { method: 'clerk' });
    trackMetaOnce(`registration:${userId}`, 'CompleteRegistration', {
      content_name: 'CartLens Account',
      status: true,
    });
  }
  else if (error.code !== '23505') console.warn('[Acquisition] Unable to save first touch:', error);
}

// A verdict means a saved trip with at least one comparable local lattice price.
// The unique history_id prevents a repeated request from inflating the event count.
export async function recordUsefulVerdict(
  supabase: SupabaseClient,
  userId: string,
  historyId: string,
  comparableItems: number,
): Promise<void> {
  if (!historyId || comparableItems < 1) return;
  const { error } = await supabase.from('user_verdict_events').insert({
    user_id: userId,
    history_id: historyId,
  });
  if (error) {
    if (error.code !== '23505') console.warn('[Acquisition] Unable to record verdict:', error);
    return;
  }
  trackGA('useful_verdict', { comparable_items: comparableItems });
  const { count, error: countError } = await supabase
    .from('user_verdict_events')
    .select('history_id', { count: 'exact', head: true })
    .eq('user_id', userId);
  if (!countError && (count || 0) > 1) {
    trackGA('repeat_useful_verdict', { comparable_items: comparableItems });
  }
}
